import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { useCallback, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { votersFor, votesFromVoters } from "../api/attendance";
import { DateOptionsWithVotes, useDateOptionsForEvent } from "../api/date-options";
import { VoteAnswer, useGetEvent } from "../api/events-api";
import { queryClient } from "../api/queryClient";
import { useLogeMembers } from "../api/user-api";
import { useAddVotes, useResetVotesForMember } from "../api/vote-api";
import { Icon } from "../components/dss/Icon";
import { FitText } from "../components/dss/FitText";
import { Eyebrow } from "../components/dss/Primitives";
import { Skeleton } from "../components/dss/Skeleton";
import { ScrollForMore } from "../components/dss/ScrollForMore";
import { SegmentedTabs } from "../components/dss/SegmentedTabs";
import DateOptionCard from "../components/votable-events/DateOptionCard";
import { useSupabaseAuth } from "../supabase/SupabaseContext";
import { DateOption } from "../types/entities";
import { assertIsDefined } from "../utils/assertion";

dayjs.locale(da);

const VotableEventPage = () => {
  const { eventId } = useParams();
  const { data: event } = useGetEvent(eventId);
  const { member } = useSupabaseAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The scrolling list of dates, watched by the "more below" button.
  const listRef = useRef<HTMLDivElement>(null);
  // Set by the "Stem" tab on the results screen: someone who deliberately
  // asked to change their vote, rather than someone who just opened the event.
  const cameToVote = (location.state as { intent?: string } | null)?.intent === "vote";
  // Which tab sent us here, if a tab did — the pill picks up from there.
  const cameFromTab = (location.state as { from?: string } | null)?.from;
  const { mutateAsync: addVotes, isLoading: isAddingVotes } = useAddVotes();
  const { mutateAsync: resetVotes, isLoading: isResetting } = useResetVotesForMember();
  const { data: dateOptions } = useDateOptionsForEvent(eventId);
  const { data: members = [] } = useLogeMembers();
  // What this member has already said, if anything. Coming back to the vote
  // shows those picks rather than a blank slate — this screen used to bounce
  // straight to the results instead, which left the "Stem" tab over there
  // looking pressable but doing nothing.
  const existingVotes = useMemo(() => {
    const mine: Record<DateOption["id"], VoteAnswer> = {};
    (dateOptions ?? []).forEach((option) => {
      const vote = option.votes.find((v) => v.user_id === member?.id);
      if (vote) mine[option.id] = vote.answer as VoteAnswer;
    });
    return mine;
  }, [dateOptions, member?.id]);

  const hasVotedBefore = Object.keys(existingVotes).length > 0;

  // Null until the member touches something, so the previous answers can fill
  // in whenever the query resolves, without an effect racing the render.
  const [edits, setEdits] = useState<Record<DateOption["id"], VoteAnswer> | null>(null);
  const selectedDates = edits ?? existingVotes;

  const datesByMonth = useMemo(() => {
    const map = new Map<string, DateOptionsWithVotes>();
    dateOptions?.forEach((option) => {
      const monthYear = dayjs(option.date).format("MMMM YYYY");
      if (!map.has(monthYear)) map.set(monthYear, []);
      map.get(monthYear)?.push(option);
    });
    return map;
  }, [dateOptions]);

  const onSave = useCallback(async () => {
    assertIsDefined(member);
    // Changing an answer means retiring the old votes first, otherwise the
    // member ends up counted twice on any date they picked both times.
    if (hasVotedBefore) {
      await resetVotes({ eventId: Number(eventId), memberId: member.id });
    }
    // A ballot answers every date on it, not only the ones picked. The page
    // has no "kan ikke" button — the toggle cycles ja → måske → back to
    // untouched — so leaving a date alone was the only way to decline it, and
    // it wrote nothing at all. That made a decline indistinguishable from
    // never having voted, and the member who can make none of the dates
    // (nothing picked, so nothing written) vanished completely. Filling the
    // gaps here, at the one moment the member has actually answered, records
    // the noes without the page having to grow a third button.
    //
    // `??`, not `||`: an existing answer of VoteAnswer.NO is 0 and must not be
    // re-read as "unanswered". dateOptions is already only the live ones —
    // useDateOptionsForEvent filters deleted at the query — so a date retired
    // by the vært never collects an afbud, and one added after this member
    // voted stays genuinely unanswered for them rather than becoming a no.
    const ballot: Record<DateOption["id"], VoteAnswer> = {};
    (dateOptions ?? []).forEach((option) => {
      ballot[option.id] = selectedDates[option.id] ?? VoteAnswer.NO;
    });
    if (Object.keys(ballot).length > 0) {
      await addVotes({ logeMemberId: member.id, selectedDates: ballot });
    }
    queryClient.invalidateQueries();
    // Replace: backing out of the results should reach the calendar, not the
    // ballot that has just been cast (which would only send them here again).
    navigate(`/vote/${eventId}/result`, { replace: true });
  }, [
    addVotes,
    dateOptions,
    eventId,
    hasVotedBefore,
    member,
    navigate,
    resetVotes,
    selectedDates,
  ]);

  const toggleSelect = (option: DateOption["id"]) => {
    setEdits((prev) => {
      const current = prev ?? existingVotes;
      if (option in current) {
        if (current[option] === VoteAnswer.YES) return { ...current, [option]: VoteAnswer.MAYBE };
        const next = { ...current };
        delete next[option];
        return next;
      }
      return { ...current, [option]: VoteAnswer.YES };
    });
  };

  // Wait for the dates too, not just the event: whether this member has
  // already voted decides which tab they land on, and guessing before the
  // votes arrive would flash the wrong one.
  if (!event || !member || !dateOptions) {
    return (
      <div className="mx-auto max-w-md bg-paper pt-safe h-app flex flex-col overflow-hidden">
        <div className="px-7 pt-2 pb-3">
          <Skeleton className="w-16 h-3" />
          <Skeleton className="w-40 h-3 mt-4" />
          <Skeleton className="w-full h-10 mt-1.5" />
          <Skeleton className="w-full h-10 mt-4 rounded-lg" />
          <Skeleton className="w-36 h-3 mt-3" />
        </div>
        <div className="px-7 pt-4 flex-1 overflow-y-auto min-h-0">
          <Skeleton className="w-24 h-4 mb-3" />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="w-full h-14 rounded-lg mb-2" />
          ))}
        </div>
        <div className="px-7 pb-5 pt-3">
          <Skeleton className="w-full h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  // The host doesn't vote on their own samling — they called it, they're
  // going. Nothing here for them to answer, so they get the results.
  if (member.id === event.host) {
    return <Navigate to={`/vote/${eventId}/result`} replace />;
  }

  // Someone who has already answered opens the event on the results — that's
  // what they came to see. Changing an answer is still one tap away on the
  // "Stem" tab, which says so explicitly and lands here. Replace, so this
  // detour doesn't become a step to back out of.
  if (hasVotedBefore && !cameToVote) {
    return <Navigate to={`/vote/${eventId}/result`} replace />;
  }

  const yesCount = Object.values(selectedDates).filter((a) => a === VoteAnswer.YES).length;
  const maybeCount = Object.values(selectedDates).filter((a) => a === VoteAnswer.MAYBE).length;
  const votedCount = new Set(
    (dateOptions ?? []).flatMap((d) => votesFromVoters(d.votes, event.host).map((v) => v.user_id)),
  ).size;
  const voterCount = votersFor(members, event.host).length;

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe h-app flex flex-col overflow-hidden">
      <div className="px-7 pt-2 pb-3">
        <button
          onClick={() => navigate("/")}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 flex items-center gap-1.5"
        >
          <Icon name="chevron-left" size={12} />
          <span>Kalender</span>
        </button>
        <Eyebrow className="mt-4">
          {votedCount} AF {voterCount || votedCount} HAR STEMT
        </Eyebrow>
        <FitText className="font-display text-[40px] leading-[0.92] tracking-tight font-extrabold mt-1.5 uppercase">
          {event.title}
        </FitText>
        <SegmentedTabs
          segments={[
            { key: "vote", label: "Stem" },
            { key: "result", label: "Resultat" },
          ]}
          className="mt-4"
          activeKey="vote"
          enterFrom={cameFromTab}
          // Replace, not push: these two are one screen with two faces, so
          // flipping between them shouldn't pile up steps that have to be
          // backed out of one by one to reach the calendar again. `from` is
          // what lets the pill on the far side carry on from where this one
          // left it, rather than blinking into place.
          onSelect={(key) =>
            key === "result" &&
            navigate(`/vote/${eventId}/result`, { replace: true, state: { from: "vote" } })
          }
        />
        {/* Who you're voting as is worth stating once, quietly — it used to be
            a card with an avatar, which gave a fact more weight than a
            decision. */}
        <div className="font-mono text-[9.5px] tracking-wide uppercase text-ink3 mt-2.5 flex items-center gap-1.5">
          <span className="text-[12px] leading-none">{member.emoji}</span>
          <span>Stemmer som {member.name}</span>
        </div>
      </div>

      {/* Relative so the "more below" button can sit at the foot of the list
          itself. The save bar is pinned over the end of it, and a date hidden
          behind that bar is a date nobody votes on. */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div ref={listRef} className="px-7 pt-4 flex-1 overflow-y-auto min-h-0">
          <div className="flex items-baseline justify-between mb-3">
            <Eyebrow>Vælg datoer</Eyebrow>
            <div className="flex items-center gap-2.5 font-mono text-[10px] text-ink3 tracking-wide">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-vote-yes inline-block" />
                JA
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-vote-maybe inline-block" />
                MÅSKE
              </span>
            </div>
          </div>
          {Array.from(datesByMonth.entries()).map(([monthYear, options]) => (
            <div key={monthYear} className="mb-4">
              <div className="font-mono text-[10px] tracking-wide text-ink3 uppercase mb-2">{monthYear}</div>
              <div className="flex flex-col gap-2">
                {options.map((dateOption) => (
                  <DateOptionCard
                    key={dateOption.id}
                    date={dateOption.date}
                    onClick={() => toggleSelect(dateOption.id)}
                    answer={selectedDates[dateOption.id]}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <ScrollForMore containerRef={listRef} label="Flere datoer" />
      </div>

      <div className="px-7 pb-5 pt-3">
        <button
          disabled={isAddingVotes || isResetting}
          onClick={onSave}
          className="w-full py-4 px-4 rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3 disabled:opacity-60"
        >
          <span className="inline-flex items-center gap-2">
            <Icon name="circle" size={7} />
            {hasVotedBefore ? "Opdatér stemmer" : "Gem stemmer"}
          </span>
          <span className="font-mono text-[10.5px] opacity-70 font-medium tracking-wide flex items-center gap-1.5">
            {yesCount} JA / {maybeCount} MÅSKE
            <Icon name="arrow-right" size={13} />
          </span>
        </button>
      </div>
    </div>
  );
};

export default VotableEventPage;
